// NOOVI: Lixeira por pipeline (retenção de 30 dias). Estado e ações da lixeira
// extraídos do componente para ficarem testáveis em isolamento (vitest).
//
// O estado da lixeira é LOCAL (a lista de descartados não faz parte da
// reatividade do board); o board só é tocado de forma indireta após um
// restore — invalidamos o cache do board (pipelineCards/resetCache) e, quando
// o pipeline atual estiver aberto, disparamos um refetch (fetchBoardByStages).
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';

export function usePipelineTrash() {
  const store = useStore();
  const { t } = useI18n();
  const { showAlert } = useAlert();

  const loading = ref(false);
  const error = ref(false);
  const items = ref([]);
  const meta = ref({
    current_page: 1,
    per_page: 25,
    total_pages: 0,
    total_count: 0,
  });
  // Set de ids de cards com restore/exclusão em andamento — alimenta o spinner
  // por item e bloqueia cliques repetidos.
  const restoringIds = ref(new Set());
  const deletingIds = ref(new Set());

  const isEmpty = computed(
    () => !loading.value && !error.value && items.value.length === 0
  );

  const isBusy = id => restoringIds.value.has(id) || deletingIds.value.has(id);

  // Reatividade do Set: trocamos a referência (Vue 3 não observa mutação in
  // place de Set profundamente para `.has`).
  const addBusy = (refSet, id) => {
    const next = new Set(refSet.value);
    next.add(id);
    refSet.value = next;
  };
  const removeBusy = (refSet, id) => {
    const next = new Set(refSet.value);
    next.delete(id);
    refSet.value = next;
  };

  const removeItem = id => {
    items.value = items.value.filter(card => card.id !== id);
    if (meta.value.total_count > 0) {
      meta.value = { ...meta.value, total_count: meta.value.total_count - 1 };
    }
  };

  const fetchTrash = async ({ pipelineId, page = 1 } = {}) => {
    if (!pipelineId) return;
    loading.value = true;
    error.value = false;
    try {
      const response = await PipelineCardsAPI.getDiscardedCards({
        pipelineId,
        page,
        perPage: meta.value.per_page,
      });
      const payload = response.data || {};
      items.value = payload.data || [];
      meta.value = payload.meta || meta.value;
    } catch (e) {
      error.value = true;
      items.value = [];
    } finally {
      loading.value = false;
    }
  };

  // Após restaurar, o card volta ao board. Invalida o cache do board e, se o
  // pipeline restaurado é o que está aberto, força o refetch por stages para
  // que o card reapareça sem reload manual. BEST-EFFORT: uma falha no refresh
  // do board NÃO pode reportar "falha ao restaurar" (o restore já teve sucesso
  // no servidor — achado Codex). Só loga.
  const refreshBoardAfterRestore = async restoredPipelineId => {
    try {
      store.dispatch('pipelineCards/resetCache');
      const openPipelineId = store.getters['pipelines/getSelectedPipeline']?.id;
      if (
        openPipelineId &&
        String(openPipelineId) === String(restoredPipelineId)
      ) {
        await store.dispatch('pipelineCards/fetchBoardByStages');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[PipelineTrash] board refresh after restore failed', e);
    }
  };

  // Restaurar/excluir o último item de uma página > 1 deixaria a lista vazia
  // numa página que não é a primeira (cards ainda existem em páginas
  // anteriores). Recarrega a página anterior nesse caso (achado Codex).
  const reloadAfterEmptyPage = async pipelineId => {
    if (items.value.length === 0 && meta.value.current_page > 1) {
      await fetchTrash({ pipelineId, page: meta.value.current_page - 1 });
    }
  };

  const restore = async card => {
    if (!card?.id || isBusy(card.id)) return;
    addBusy(restoringIds, card.id);
    try {
      await PipelineCardsAPI.restoreCard(card.id);
      removeItem(card.id);
      showAlert(t('PIPELINE.TRASH.RESTORE.SUCCESS'), { type: 'success' });
      // Pós-sucesso (não afeta o resultado do restore): refresh do board +
      // ajuste de paginação.
      await refreshBoardAfterRestore(card.pipeline_id);
      await reloadAfterEmptyPage(card.pipeline_id);
    } catch (e) {
      showAlert(t('PIPELINE.TRASH.RESTORE.ERROR'), { type: 'error' });
    } finally {
      removeBusy(restoringIds, card.id);
    }
  };

  const permanentlyDelete = async card => {
    if (!card?.id || isBusy(card.id)) return;
    addBusy(deletingIds, card.id);
    try {
      await PipelineCardsAPI.permanentlyDeleteCard(card.id);
      removeItem(card.id);
      showAlert(t('PIPELINE.TRASH.DELETE.SUCCESS'), { type: 'success' });
      await reloadAfterEmptyPage(card.pipeline_id);
    } catch (e) {
      showAlert(t('PIPELINE.TRASH.DELETE.ERROR'), { type: 'error' });
    } finally {
      removeBusy(deletingIds, card.id);
    }
  };

  const reset = () => {
    loading.value = false;
    error.value = false;
    items.value = [];
    meta.value = {
      current_page: 1,
      per_page: 25,
      total_pages: 0,
      total_count: 0,
    };
    restoringIds.value = new Set();
    deletingIds.value = new Set();
  };

  return {
    loading,
    error,
    items,
    meta,
    restoringIds,
    deletingIds,
    isEmpty,
    isBusy,
    isRestoring: id => restoringIds.value.has(id),
    isDeleting: id => deletingIds.value.has(id),
    fetchTrash,
    restore,
    permanentlyDelete,
    reset,
  };
}
